import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
  ChatSettings,
  ChatSettingsRanges,
  DeploymentModel,
  PersonaModel,
  PersonaService, ReasoningEffort,
} from 'digiteq-ai-portal-client-lib';
import { AppSettingsService } from '../../../services/app-settings.service';
import { forkJoin, Subscription } from 'rxjs';
import { ErrorService } from '../../../services/error.service';
import { SettingsItems } from '../../../models/settings-items';
import { DropdownChangeEvent } from 'primeng/dropdown';


@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
class SettingsComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  tooltips = new Map<string, string>();
  form: FormGroup;
  settingsRanges: ChatSettingsRanges;
  settingsItemsEnum = SettingsItems;

  private _selectedModel: number | undefined;

  protected readonly reasoningEffortLevelOptions: { label: string; value: ReasoningEffort }[] =
    Object.entries(ReasoningEffort).map(([label, value]) => ({
      label,
      value,
    }));

  public get selectedModel(): number | undefined {
    return this._selectedModel;
  }
  public set selectedModel(value: number | undefined) {
    const enabled = this.models.find((model) => model.id === value)?.enabled

    if (!enabled) {
      return
    }

    this._selectedModel = value;
  }

  selectedPersona: PersonaModel | undefined;
  personas: PersonaModel[];

  get models(): DeploymentModel[] {
    return this.settingsService.models;
  }

  set models(value: DeploymentModel[]) {
    this.settingsService.models = value;
  }

  get defaultPersona(): PersonaModel {
    return this.personas?.find(p => p.default);
  }

  constructor(
    private fb: FormBuilder,
    public settingsService: AppSettingsService,
    private errorService: ErrorService,
    private personaService: PersonaService,
  ) {
  }

  ngOnInit() {
    this.tooltips.set(SettingsItems.persona, 'Choose chat persona. Each persona has different settings adapted to be most useful for your use case.');
    this.tooltips.set(SettingsItems.prompt, 'Give the model instructions about how it should behave and any context it should reference ' +
      'when generating a response. You can describe the assistant’s personality, tell it what it should and shouldn’t answer,' +
      ' and tell it how to format responses. There’s no token limit for this section, but it will be included with every ' +
      'API call, so it counts against the overall token limit.');
    this.tooltips.set(SettingsItems.maxTokens, 'Set a limit on the number of tokens per model response. One token is roughly 4 characters ' +
      'for typical English text. Minimum 100.');
    this.tooltips.set(SettingsItems.lastMessages, 'Set how many previous messages will be included in the model\'s context when ' +
      'generating a response. Minimum 1.');
    this.tooltips.set(SettingsItems.reasoningEffortLevel, 'Set the level of reasoning effort the model should apply when generating a response.');

    let settingsChangeSubscription = this.settingsService.$settingsChange.subscribe(settings => {
      this.form.setValue(settings);
      this.selectedModel = settings.deploymentId
    })
    this.subscriptions.push(settingsChangeSubscription);

    forkJoin([
      this.settingsService.getSettingsRanges(),
      this.personaService.getPersonas(),
      this.settingsService.getModels(),
    ])
      .subscribe({
        next: ([settingsRanges, personas, models]) => {
          this.settingsRanges = settingsRanges;
          this.personas = personas;
          this.settingsService.models = models;
          this.selectedPersona = this.getPersonaById(this.settingsService.savedPersonaId) || this.defaultPersona;
          this.settingsService.savedPersonaId = this.selectedPersona.id;
          this.prepareForm();
        },
        error: err => {
          this.errorService.handleError(err, 'Could not get the chat settings.')
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  unsubscribe(): void {
    if (this.subscriptions?.length > 0) {
      this.subscriptions.forEach(s => s.unsubscribe());
    }
  }

  subscribeOnSliderValues() {
    const sliderFormControls = ['maxTokens', 'lastMessages'];
    sliderFormControls.forEach(sliderFormControlName => {
      let formControlSubscription = this.form.controls[sliderFormControlName].valueChanges.subscribe(value => {
        this.form.controls[sliderFormControlName].setValue(value, {onlySelf: true, emitEvent: false})
      })
      this.subscriptions.push(formControlSubscription);
    })
  }

  modelChanged(event: DropdownChangeEvent) {
    if (!event.value) {
      return;
    }

    const model = this.models.find((model) => model.id === event.value);

    if (!model?.enabled) {
      return
    }

    this.form.controls['deploymentId'].setValue(event.value);
    this.settingsService.savedSettings = {
      ...this.settingsService.savedSettings,
      deploymentId: event.value,
    }
    const maxTokensValue = this.form.controls['maxTokens'].value;
    if(maxTokensValue > model.maxResponseTokens) {
      this.form.controls['maxTokens'].setValue(model.maxResponseTokens);
    }

    this.settingsService.selectModel(this.selectedModel);
  }

  prepareForm() {
    this.form = this.fb.group(this.settingsService.savedSettings);
    this.selectedModel =  this.settingsService.selectedModel.id

    this.unsubscribe();

    let currentSettingsSubscription = this.form.valueChanges.subscribe(currentSettings => {
      this.settingsService.savedSettings = currentSettings;
    })
    this.subscriptions.push(currentSettingsSubscription);
    this.subscribeOnSliderValues();
  }

  resetToDefaultSettings() {
    const defaultSettings = this.convertPersonaToSettings(this.selectedPersona);
    if (defaultSettings) {
      this.settingsService.savedSettings = defaultSettings;
      this.settingsService.savedPersonaId = this.selectedPersona.id;
      this.prepareForm();
      this.settingsService.selectModel(this.selectedModel);
    }
  }

  getTooltip(field: string): string {
    return this.tooltips.get(field) || '';
  }

  handlePersonaChanged(event: DropdownChangeEvent) {
    if (!event.value) {
      return;
    }
    const settingsOfPersona = this.convertPersonaToSettings(event.value);
    this.settingsService.savedPersonaId = event.value.id;
    this.settingsService.savedSettings = settingsOfPersona;
    this.form.setValue(settingsOfPersona);
  }

  convertPersonaToSettings(persona: PersonaModel): ChatSettings {
    return {
      deploymentId: persona.deploymentId,
      prompt: persona.prompt,
      maxTokens: persona.maxTokens,
      lastMessages: persona.lastMessages,
      reasoningEffortLevel: persona.reasoningEffortLevel || ReasoningEffort.Medium
    }
  }

  getPersonaById(id: number): PersonaModel {
    return this.personas.find(p => p.id === id);
  }

  onItemClicked(event: MouseEvent, item: DeploymentModel) {
    if (!item.enabled) {
      event.stopPropagation();
      event.preventDefault();
    }
  }
 }

export default SettingsComponent
